import type {
  ClusterObjects,
  FetchResponse,
  KubernetesFetchError,
} from '@backstage/plugin-kubernetes-common';

export type Pod = Extract<FetchResponse, { type: 'pods' }>['resources'][number];

export const CLUSTER_ORDER = ['preprod', 'mgmt', 'prod'];
export const DEFAULT_CLUSTER = 'prod';

const rank = (name: string) => {
  const i = CLUSTER_ORDER.indexOf(name);
  return i === -1 ? CLUSTER_ORDER.length : i;
};

export function orderClusters(items: ClusterObjects[]): ClusterObjects[] {
  return [...items].sort(
    (a, b) =>
      rank(a.cluster.name) - rank(b.cluster.name) ||
      a.cluster.name.localeCompare(b.cluster.name),
  );
}

export function defaultCluster(items: ClusterObjects[]): string | undefined {
  const names = orderClusters(items).map(c => c.cluster.name);
  return names.includes(DEFAULT_CLUSTER) ? DEFAULT_CLUSTER : names[0];
}

export function podsOf(cluster: ClusterObjects): Pod[] {
  return cluster.resources.flatMap(r => (r.type === 'pods' ? r.resources : []));
}

export function describeFetchError(error: KubernetesFetchError): string {
  if ('message' in error) {
    return `${error.errorType}: ${error.message}`;
  }
  const where = error.resourcePath ? ` on ${error.resourcePath}` : '';
  const code = error.statusCode ? ` (${error.statusCode})` : '';
  return `${error.errorType}${code}${where}`;
}

const TRANSIENT_WAITING = new Set(['ContainerCreating', 'PodInitializing']);

export function hasErrors(pod: Pod): boolean {
  if (pod.status?.phase === 'Failed') return true;
  const statuses = [
    ...(pod.status?.initContainerStatuses ?? []),
    ...(pod.status?.containerStatuses ?? []),
  ];
  return statuses.some(s => {
    const waiting = s.state?.waiting?.reason;
    if (waiting && !TRANSIENT_WAITING.has(waiting)) return true;
    const exit = s.state?.terminated?.exitCode;
    return exit !== undefined && exit !== 0;
  });
}

export function containersReady(pod: Pod): string {
  const statuses = pod.status?.containerStatuses ?? [];
  return `${statuses.filter(s => s.ready).length}/${statuses.length}`;
}

export function totalRestarts(pod: Pod): number {
  return (pod.status?.containerStatuses ?? []).reduce(
    (sum, s) => sum + s.restartCount,
    0,
  );
}

export type ContainerIssue = { container: string; reason: string; ok: boolean };

export function containerIssues(pod: Pod): ContainerIssue[] {
  return (pod.status?.containerStatuses ?? []).flatMap(s => {
    const reasons = [s.state?.waiting?.reason, s.state?.terminated?.reason];
    return reasons
      .filter((r): r is string => Boolean(r))
      .map(reason => ({
        container: s.name,
        reason,
        ok: reason === 'Completed',
      }));
  });
}

export const formatMillicores = (value: number | string): string =>
  `${(parseFloat(String(value)) * 1000).toFixed(0)}m`;

export const bytesToMiB = (value: number | string): string =>
  `${(parseFloat(String(value)) / 1024 / 1024).toFixed(0)}MiB`;

export function percent(
  current: number | string,
  declared: number | string,
): number {
  const c = parseFloat(String(current));
  const d = parseFloat(String(declared));
  return d > 0 ? Math.round((c / d) * 100) : 0;
}

export function summarize(pods: Pod[]) {
  return {
    total: pods.length,
    running: pods.filter(p => p.status?.phase === 'Running').length,
    withErrors: pods.filter(hasErrors).length,
  };
}
